import { index } from "~/api/contractBalanceOverview";
import * as types from "../mutation-types";
import store from "../../store";

const VALID_SCALES = ["day", "week", "month"];
const DEFAULT_SCALE = "day";
const DEFAULT_COUNTS = { day: 7, week: 4, month: 3 };

function normalizeScale(scale) {
  return VALID_SCALES.includes(scale) ? scale : DEFAULT_SCALE;
}

function createRequestKey(contractId, scale) {
  return `${contractId}:${scale}`;
}

const requestMap = new Map();

export const state = {
  dataByScale: {},
  isLoadingByScale: {},
  updatedAtByScale: {}
};

export const mutations = {
  [types.SET_IS_LOADING_CONTRACT_BALANCE_OVERVIEW](state, payload) {
    const scale = normalizeScale(payload?.scale);
    state.isLoadingByScale = {
      ...state.isLoadingByScale,
      [scale]: Boolean(payload?.status)
    };
  },
  [types.SET_CONTRACT_BALANCE_OVERVIEW_DATA](state, payload) {
    const scale = normalizeScale(payload?.scale);
    const data = payload?.data ?? {};

    state.dataByScale = {
      ...state.dataByScale,
      [scale]: {
        withdrawals: data.withdrawals ?? [],
        deposits: data.deposits ?? [],
        scale: data.scale ?? scale,
        count: data.count ?? null
      }
    };
  },
  [types.CLEAR_CONTRACT_BALANCE_OVERVIEW_DATA](state) {
    state.dataByScale = {};
    state.isLoadingByScale = {};
    state.updatedAtByScale = {};
  },
  [types.SET_CONTRACT_BALANCE_OVERVIEW_UPDATED_AT](state, payload) {
    const scale = normalizeScale(payload?.scale);
    state.updatedAtByScale = {
      ...state.updatedAtByScale,
      [scale]: payload?.timestamp || null
    };
  }
};

export const actions = {
  clearContractBalanceOverviewCache({ commit }) {
    requestMap.clear();
    commit(types.CLEAR_CONTRACT_BALANCE_OVERVIEW_DATA);
  },

  async fetchContractBalanceOverview({ commit, state: moduleState }, payload = {}) {
    const activeContractId =
      payload?.contractId || store.getters["contract/activeContract"]?.id;
    const scale = normalizeScale(payload?.scale);
    const count = payload?.count ?? DEFAULT_COUNTS[scale] ?? 7;

    if (!activeContractId) {
      commit(types.SET_CONTRACT_BALANCE_OVERVIEW_DATA, { scale, data: {} });
      commit(types.SET_IS_LOADING_CONTRACT_BALANCE_OVERVIEW, { scale, status: false });
      return null;
    }

    const requestKey = createRequestKey(activeContractId, scale);

    if (requestMap.has(requestKey)) {
      return requestMap.get(requestKey);
    }

    if (!payload?.force && moduleState.dataByScale[scale] && moduleState.updatedAtByScale[scale]) {
      return null;
    }

    commit(types.SET_IS_LOADING_CONTRACT_BALANCE_OVERVIEW, { scale, status: true });

    const requestPromise = index(activeContractId, { scale, count })
      .then((response) => {
        const responseData = response?.data?.data ?? response?.data ?? response ?? {};

        commit(types.SET_CONTRACT_BALANCE_OVERVIEW_DATA, { scale, data: responseData });
        commit(types.SET_CONTRACT_BALANCE_OVERVIEW_UPDATED_AT, { scale, timestamp: Date.now() });

        return response;
      })
      .finally(() => {
        commit(types.SET_IS_LOADING_CONTRACT_BALANCE_OVERVIEW, { scale, status: false });
        requestMap.delete(requestKey);
      });

    requestMap.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      throw error;
    }
  }
};

export const getters = {
  getByScale: (state) => (scale = DEFAULT_SCALE) => {
    const resolved = normalizeScale(scale);
    return state.dataByScale[resolved] || null;
  },
  withdrawalsByScale: (state) => (scale = DEFAULT_SCALE) => {
    const resolved = normalizeScale(scale);
    return state.dataByScale[resolved]?.withdrawals ?? [];
  },
  depositsByScale: (state) => (scale = DEFAULT_SCALE) => {
    const resolved = normalizeScale(scale);
    return state.dataByScale[resolved]?.deposits ?? [];
  },
  isLoadingByScale: (state) => (scale = DEFAULT_SCALE) => {
    const resolved = normalizeScale(scale);
    return Boolean(state.isLoadingByScale[resolved]);
  },
  updatedAtByScale: (state) => (scale = DEFAULT_SCALE) => {
    const resolved = normalizeScale(scale);
    return state.updatedAtByScale[resolved] || null;
  },

  // backwards-compat
  withdrawals: (state) => state.dataByScale[DEFAULT_SCALE]?.withdrawals ?? [],
  deposits: (state) => state.dataByScale[DEFAULT_SCALE]?.deposits ?? [],
  isLoading: (state) => Boolean(state.isLoadingByScale[DEFAULT_SCALE]),
  updatedAt: (state) => state.updatedAtByScale[DEFAULT_SCALE] || null,
  scale: (state) => state.dataByScale[DEFAULT_SCALE]?.scale ?? null,
  count: (state) => state.dataByScale[DEFAULT_SCALE]?.count ?? null
};
